/**
 * Event Module Registry
 *
 * Fully API-driven. No hardcoded module configs.
 * All event module data is loaded from the HRDC Event backend at bootstrap.
 * If the backend is down, the registry is empty and event modules are unavailable.
 */

// Shared event API URL (used for loadFromApi and as default serviceUrl)
const EVENT_API_URL = process.env.VUE_APP_SERVICE_URL_HRDC_EVENT
  || process.env.VUE_APP_SERVICE_URL_HRDC_EINVOICING
  || process.env.VUE_APP_SERVICE_URL_HRDC_TAX_SUMMIT
  || process.env.VUE_APP_SERVICE_URL_HRDC_TPDIY
  || process.env.VUE_APP_SERVICE_URL_HRDC_STAMP_DUTY;

// Registry starts empty - populated by loadFromApi()
let EVENT_MODULES = {};
// All modules including inactive (for dashboard display)
let ALL_EVENT_MODULES = {};

/**
 * Canonical crmViewKey derivation — the SINGLE source of truth.
 *
 * crmViewKey is the lookup key for a module's saved CRM views
 * (crmView/list?modelKey=). It MUST be produced identically everywhere it is
 * generated (this registry's enrichConfig + the Event Module settings form),
 * otherwise a module's saved views silently fail to load. Rules:
 *   1. lowercase
 *   2. hyphens & whitespace -> "_"
 *   3. drop every char outside [a-z0-9_] (colons, slashes, parens, ...)
 *   4. collapse repeated "_" and trim leading/trailing "_"
 *   5. strip a leading "hrdc_" — the prefix belongs on serviceKey, not the
 *      view key (so "HRDC Cash Flow Planning" -> "cash_flow_planning_application",
 *      consistent with "tpdiy_application")
 *   6. suffix with "_application"
 *
 * @param {string} source name1 (preferred) or serviceKey
 * @returns {string} e.g. "cash_flow_planning_application" ("" when source is empty)
 */
export function deriveCrmViewKey(source) {
  if (!source) return "";
  const prefix = source
    .toLowerCase()
    .replace(/-/g, "_")
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .replace(/^hrdc_/, "");
  return prefix ? `${prefix}_application` : "";
}

/**
 * Enrich a raw config from API with derived fields.
 * Adds menuActionRoutes, serviceUrl, and crmViewKey that aren't stored in the DB.
 */
function enrichConfig(config) {
  // Derive menu action routes from routePrefix
  if (!config.menuActionRoutes) {
    const r = config.routePrefix;
    config.menuActionRoutes = {
      editApplication: `${r}EditApplication`,
      generalInfo: `${r}AddGeneralInfo`,
      sendEmail: `${r}SendEmailToClient`,
      revertForm: `${r}ClientRevertForm`,
      manualInvoice: `${r}ManualInvoiceToHrdc`,
      claimSubmit: `${r}ClaimSubmitToHrdc`,
      claimApproved: `${r}ClaimApprovedFromHrdc`,
      paymentReceived: `${r}PaymentReceivedFromHrdc`,
    };
  }

  // All event modules share the same API URL
  if (!config.serviceUrl) {
    config.serviceUrl = EVENT_API_URL;
  }

  // Derive (or normalize) crmViewKey via the single shared sanitizer.
  // Missing -> derive from name1/serviceKey. Present -> re-run through the
  // canonical rule so any value stored by an older derivation (e.g. a leftover
  // "hrdc_" prefix or stray characters) collapses to the same key the rest of
  // the app expects, instead of silently pointing at views that won't load.
  if (!config.crmViewKey) {
    config.crmViewKey = deriveCrmViewKey(config.name1 || config.serviceKey);
  } else {
    config.crmViewKey = deriveCrmViewKey(
      config.crmViewKey.replace(/_application$/, "")
    );
  }

  return config;
}

/**
 * Load event module configs from the backend API.
 * This is the ONLY way to populate the registry.
 *
 * @param {string} [apiUrl] - Override API URL
 * @returns {Promise<object[]>} Array of event configs
 */
export async function loadFromApi(apiUrl) {
  const url = apiUrl || EVENT_API_URL;
  if (!url) {
    return [];
  }

  try {
    const response = await fetch(`${url}/v1/en/console/event_module/list`);
    const result = await response.json();

    if (result.status && Array.isArray(result.data) && result.data.length > 0) {
      EVENT_MODULES = {};
      ALL_EVENT_MODULES = {};
      for (const raw of result.data) {
        const enriched = enrichConfig({ ...raw });
        ALL_EVENT_MODULES[raw.serviceKey] = enriched;
        // Only register active modules for routes/store/models
        if (raw.isActive) {
          EVENT_MODULES[raw.serviceKey] = enriched;
        }
      }
    } else {
      EVENT_MODULES = {};
      ALL_EVENT_MODULES = {};
    }
  } catch (err) {
    EVENT_MODULES = {};
    ALL_EVENT_MODULES = {};
  }

  return getAllEventConfigs();
}

// --- Public API ---

export function getEventConfig(serviceKey) {
  return EVENT_MODULES[serviceKey] || null;
}

export function getAllEventConfigs() {
  return Object.values(EVENT_MODULES);
}

export function getAllEventConfigsIncludingInactive() {
  return Object.values(ALL_EVENT_MODULES);
}

export function isEventModule(serviceKey) {
  return serviceKey in EVENT_MODULES;
}

export function getEventTypeMap() {
  const map = {};
  for (const config of Object.values(EVENT_MODULES)) {
    map[config.serviceKey] = config.hrdcType;
  }
  return map;
}

export function getMenuActionsMap() {
  const map = {};
  for (const config of Object.values(EVENT_MODULES)) {
    map[config.serviceKey] = [
      { title: "Edit Details", route: config.menuActionRoutes.editApplication },
      { title: "General Info", route: config.menuActionRoutes.generalInfo },
      { title: "Send HRDC Email to Client", route: config.menuActionRoutes.sendEmail },
      { title: "Grant Approval & Revert Form", route: config.menuActionRoutes.revertForm },
      { title: "Manual Invoice to HRDC", route: config.menuActionRoutes.manualInvoice },
      { title: "Claim Submitted to HRDC", route: config.menuActionRoutes.claimSubmit },
      { title: "Claim Approved from HRDC", route: config.menuActionRoutes.claimApproved },
      { title: "Payment Received from HRDC", route: config.menuActionRoutes.paymentReceived },
    ];
  }
  return map;
}

export function generateMenuConfig(config) {
  return [
    {
      name: "Applications",
      icon: "mdi-file-document-outline",
      route: {
        name: `${config.routePrefix}Application`,
        params: { modelKey: "application" },
        query: { viewId: "all" },
      },
    },
    {
      name: "Refund AP to CIMB",
      slug: "refund-ap-to-cimb",
      icon: "mdi-bank-outline",
      route: {
        name: `${config.routePrefix}RefundEmailApToCimb`,
        params: { modelKey: "refundApToCimb" },
        query: { viewId: "all" },
      },
      disabledForRoles: ["taxpod-salesperson"],
    },
    {
      name: "Report Analysis",
      icon: "mdi-file-chart",
      route: {
        name: `${config.routePrefix}ReportAnalysis`,
        params: { modelKey: "reportAnalysis" },
      },
      disabledForRoles: ["taxpod-salesperson"],
    },
    {
      name: "Settings",
      icon: "mdi-cog-outline",
      route: {
        name: `${config.routePrefix}Settings`,
        params: { modelKey: "hrdcSettings" },
      },
      disabledForRoles: ["taxpod-salesperson"],
      onlyAllowForRoles: ["hrdc-super-admin", "superadmin"],
    },
  ];
}

export default EVENT_MODULES;
